import { Box, Typography } from "@mui/material";
import {
  AthletesModel,
  AutoField,
  Exercise,
  Exercises,
  getAttemptStatus,
  getAttemptValue,
  LiftField,
  LiftStatus,
  OverturnColorField,
  OverturnField,
  PersonalBestField,
  RefereeColorField,
} from "../../../../util/src";
import PowerScoutIcon from "../../img/powerscout-icon.svg";

interface CurrentAthleteWidgetProps {
  currentAthlete: AthletesModel;
  currentLift: LiftField;
  currentMouv: Exercise;
  attemptNumber: number;
  records?: records[];
  showPR: boolean;
}

import PropTypes from "prop-types";
import dico from "../../../../util/src/language/dico";
import RenderRecord from "../../misc/RenderRecord";
import { records } from "@prisma-local/client";
import Shape from "../../misc/Shape";
import ReplayIcon from "@mui/icons-material/Replay";
import backgroundSvg from "../../img/Challenge_Hiver_Widget.svg";
import backgroundPng from "../../img/Challenge_Hiver_Widget.png";
import LiftPill from "./LiftPill";
import { motion, AnimatePresence } from "framer-motion";

const CurrentAthleteWidget: React.FC<CurrentAthleteWidgetProps> = ({
  currentAthlete,
  currentLift,
  currentMouv,
  attemptNumber,
  records,
  showPR,
}) => {
  const getBackgroundColor = (status: LiftStatus) => {
    switch (status) {
      case LiftStatus.LIFT:
        return "rgb(88,193,106)"; // Vert pour réussite
      case LiftStatus.NO_LIFT:
        return "rgb(202,87,106)"; // Rouge pour échec
      default:
        return "white";
    }
  };

  const getBestLift = (lift: "squat" | "bench" | "deadlift") => {
    for (let i = 3; i >= 1; i--) {
      if (
        currentAthlete[`${lift}${i}Status` as keyof AthletesModel] ===
        LiftStatus.LIFT
      )
        return currentAthlete[`${lift}${i}` as keyof AthletesModel] as number;
    }

    if (
      currentAthlete[`${lift}${1}Status` as keyof AthletesModel] ===
      LiftStatus.ANNONCED
    )
      return currentAthlete[`${lift}${1}` as keyof AthletesModel] as number;

    return 0;
  };

  const getPersonalBestKey = (
    lift: "squat" | "bench" | "deadlift",
  ): PersonalBestField => {
    return `best_${lift}_kg` as PersonalBestField;
  };

  const renderAttempts = () => {
    if (!currentAthlete || !currentLift) return null;

    const attempts = Array.from({ length: attemptNumber }, (_, i) => i + 1);

    return (
      <Box sx={{ display: "flex", flexDirection: "row" }}>
        {attempts.map((attempt) => {
          const status = getAttemptStatus(currentAthlete, currentLift, attempt);
          const value = getAttemptValue(currentAthlete, currentLift, attempt);
          const backgroundColor = getBackgroundColor(status);
          const base = (currentMouv.name + attempt) as LiftField;
          const autoKey = `${base}Auto` as AutoField;
          const overturnKey = `${base}Overturn` as OverturnField;
          const leftColorKey = `${base}LeftColor` as RefereeColorField;
          const chiefColorKey = `${base}ChiefColor` as RefereeColorField;
          const rightColorKey = `${base}RightColor` as RefereeColorField;
          const overturnColorKey = `${base}OverturnColor` as OverturnColorField;
          const isAuto = currentAthlete[autoKey];
          const isOverturn = currentAthlete[overturnKey];
          const leftColor = currentAthlete[leftColorKey];
          const chiefColor = currentAthlete[chiefColorKey];
          const rightColor = currentAthlete[rightColorKey];
          const overturnColor = currentAthlete[overturnColorKey];

          return (
            <motion.div
              key={attempt}
              initial={{ opacity: 0, y: 20, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{
                duration: 0.25,
                delay: attempt * 0.05,
              }}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                marginInline: 8,
              }}
            >
              <Box
                sx={{
                  minWidth: 130,
                  px: 4,
                  py: 1.5,
                  borderRadius: "25px",
                  backgroundColor,
                  boxShadow: "0 10px 25px rgba(0,0,0,0.35)",
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  "&::after": {
                    content: '""',
                    boxShadow: "0 10px 25px rgba(0,0,0,0.35)",
                    position: "absolute",
                    inset: 0,
                    borderRadius: "25px",
                    border:
                      status === LiftStatus.ANNONCED
                        ? "3px solid rgb(202,87,106)"
                        : "none",
                    animation:
                      status === LiftStatus.ANNONCED
                        ? "blink-current-lift 2s infinite"
                        : "none",
                    pointerEvents: "none",
                  },
                }}
              >
                <RenderRecord
                  athlete={currentAthlete}
                  lift={currentLift}
                  total={false}
                  weight={Number(value)}
                  records={records ?? []}
                  sx={{
                    position: "absolute",
                    top: 0,
                    right: 0,
                    zIndex: 3,
                  }}
                  flagWidth={30}
                  flagHeight={20}
                  blink={status === LiftStatus.ANNONCED}
                />
                {isAuto && !isOverturn && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      gap: "1px",
                      position: "absolute",
                      bottom: 5,
                    }}
                  >
                    <Shape
                      variant="circle"
                      size={10}
                      color={leftColor ?? ""}
                      fill={leftColor ?? ""}
                    />
                    <Shape
                      variant="circle"
                      size={10}
                      color={chiefColor ?? ""}
                      fill={chiefColor ?? ""}
                    />
                    <Shape
                      variant="circle"
                      size={10}
                      color={rightColor ?? ""}
                      fill={rightColor ?? ""}
                    />
                  </Box>
                )}
                {isAuto && isOverturn && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      gap: "1px",
                      position: "absolute",
                      bottom: 5,
                    }}
                  >
                    <Shape
                      variant="circle"
                      size={10}
                      color={overturnColor ?? ""}
                      fill={overturnColor ?? ""}
                    />
                    <ReplayIcon sx={{ color: "#fff", fontSize: "10px" }} />
                  </Box>
                )}
                <Typography
                  variant="h4"
                  sx={{
                    color: "#000",
                    fontWeight: 800,
                    textAlign: "center",
                  }}
                >
                  {value?.toString() || "-"}
                </Typography>
              </Box>
            </motion.div>
          );
        })}
      </Box>
    );
  };

  const renderPersonalBest = () => {
    return (
      <Box sx={{ width: "35%", overflow: "hidden" }}>
        <motion.div
          initial={false}
          animate={showPR ? "visible" : "hidden"}
          variants={{
            visible: {
              opacity: 1,
              y: 0,
              scale: 1,
              transition: { duration: 0.4, ease: [0.22, 0.61, 0.36, 1] },
            },
            hidden: {
              opacity: 0,
              y: 40,
              scale: 0.95,
              transition: { duration: 0.3, ease: [0.22, 0.61, 0.36, 1] },
            },
          }}
          style={{ width: "100%" }}
        >
          <Box
            sx={{
              display: "flex",
              position: "relative",
              flexDirection: "column",
              gap: 2,
              pr: 3,
              pl: 6,
              py: 2,
              borderRadius: "15px",
              backgroundImage: `url(${backgroundSvg})`,
              backgroundRepeat: "no-repeat",
              backgroundSize: "cover",
              backgroundPosition: "center",
              boxShadow: "0 18px 35px rgba(0,0,0,0.35)",
              overflow: "hidden",
              width: "100%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="h3" fontSize={"2em"} sx={textStyle}>
                {dico().widget.personalBest}
              </Typography>
              <Box
                sx={{
                  position: "relative",
                  width: "50px",
                  height: "50px",
                }}
              >
                <img
                  src={PowerScoutIcon}
                  alt="Powerscout"
                  style={{
                    position: "absolute",
                    top: "50%",
                    right: "50%",
                    transform: "translate(50%, -50%)",
                    width: 100,
                    height: 100,
                  }}
                />
              </Box>
            </Box>

            {Exercises.map((exercise) => {
              const personalBestValue =
                currentAthlete.powerscoutBests?.[
                  getPersonalBestKey(exercise.name)
                ];
              return (
                <Box
                  key={exercise.name}
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: "2em",
                    }}
                  >
                    <Typography variant="h4" fontSize={"1.8em"} sx={textStyle}>
                      {dico().widget[`${exercise.name}Pr`]}
                    </Typography>
                  </Box>
                  <LiftPill
                    fontSize="1.8em"
                    value={
                      (personalBestValue ?? 0) + dico().widget.kg.toUpperCase()
                    }
                    color="#fff"
                    backgroundColor="rgb(94,145,174)"
                  />
                </Box>
              );
            })}

            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "2em",
                }}
              >
                <Typography variant="h4" fontSize={"1.8em"} sx={textStyle}>
                  {dico().widget.totalPr}
                </Typography>
              </Box>
              <LiftPill
                fontSize="1.8em"
                value={
                  (currentAthlete.powerscoutBests?.best_total_kg ?? 0) +
                  dico().widget.kg.toUpperCase()
                }
                color="#fff"
                backgroundColor="rgb(94,145,174)"
              />
            </Box>
          </Box>
        </motion.div>
      </Box>
    );
  };

  const textStyle = {
    color: "#002439",
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: 1,
    opacity: 1,
    textShadow: `
      0px 2px 4px #fff,
      0px 0px 8px #fff
    `,
  } as const;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        gap: 1,
        position: "relative",
      }}
    >
      <style>{`
        @keyframes blink-current-lift {
          0% { opacity: 1; }
          50% { opacity: 0.3; }
          100% { opacity: 1; }
        }
      `}</style>

      {/* Bloc Personal Best animé */}
      <Box
        sx={{
          display: "flex",
          gap: 1,
          flexDirection: "column",
          opacity:
            currentAthlete.powerscoutBests &&
            currentAthlete.powerscoutBests.best_squat_kg
              ? 1
              : 0,
        }}
      >
        {renderPersonalBest()}
      </Box>

      {/* Bloc principal athlète animé avec AnimatePresence */}
      <AnimatePresence mode="wait">
        <motion.div
          key={`${currentAthlete.prenom}-${currentAthlete.nom}-${currentMouv.name}-${attemptNumber}`}
          initial={{ opacity: 0, x: 60, scale: 0.95 }}
          animate={{
            opacity: 1,
            x: 0,
            scale: 1,
            transition: {
              duration: 1,
              ease: [0.22, 0.61, 0.36, 1],
              delay: 0,
            },
          }}
          exit={{
            opacity: 0,
            x: -60,
            scale: 0.95,
            transition: {
              duration: 1,
              ease: [0.22, 0.61, 0.36, 1],
              delay: 0,
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              position: "relative",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 4,
              px: 6,
              py: 3,
              borderRadius: "25px",
              boxShadow: "0 18px 35px rgba(0,0,0,0.35)",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                backgroundImage: `url(${backgroundSvg})`,
                backgroundRepeat: "no-repeat",
                backgroundSize: "cover",
                backgroundPosition: "center",
                opacity: 1,
                zIndex: 1,
              }}
            />

            {/* Bloc catégorie + nom */}
            <Box sx={{ display: "flex", zIndex: 2, flexDirection: "column" }}>
              <Typography variant="h4" sx={textStyle}>
                {currentAthlete.catPoid?.nom} {currentAthlete.catAge?.nom}
              </Typography>

              <Typography variant="h3" sx={textStyle}>
                {currentAthlete.prenom} {currentAthlete.nom}
              </Typography>
            </Box>

            {/* SQUAT / BENCH / DEADLIFT */}
            <Box
              sx={{
                display: "flex",
                gap: 4,
                flexGrow: 1,
                zIndex: 2,
                justifyContent: "center",
              }}
            >
              {/* colonne squat */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Typography variant="h5" sx={textStyle}>
                  {dico().widget.squat}
                </Typography>

                {currentMouv.name === "squat" ? (
                  renderAttempts()
                ) : (
                  <LiftPill
                    value={getBestLift("squat")}
                    color="#fff"
                    backgroundColor="rgb(94,145,174)"
                  />
                )}
              </Box>

              {/* colonne bench */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Typography variant="h5" sx={textStyle}>
                  {dico().widget.bench}
                </Typography>

                {currentMouv.name === "bench" ? (
                  renderAttempts()
                ) : (
                  <LiftPill
                    value={getBestLift("bench")}
                    color={currentMouv.name === "squat" ? "#000" : "#fff"}
                    backgroundColor={
                      currentMouv.name === "squat"
                        ? "rgb(152,165,166)"
                        : "rgb(94,145,174)"
                    }
                  />
                )}
              </Box>

              {/* colonne deadlift */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Typography variant="h5" sx={textStyle}>
                  {dico().widget.deadlift}
                </Typography>

                {currentMouv.name === "deadlift" ? (
                  renderAttempts()
                ) : (
                  <LiftPill
                    value={getBestLift("deadlift")}
                    color="#000"
                    backgroundColor="rgb(152,165,166)"
                  />
                )}
              </Box>
            </Box>

            {/* Total */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1,
                zIndex: 2,
              }}
            >
              <Typography variant="h5" sx={textStyle}>
                {dico().widget.total}
              </Typography>
              <LiftPill
                value={currentAthlete.totalkg}
                color="#000"
                backgroundColor="rgb(241,201,113)"
              />
            </Box>

            {/* Image PNG par dessus */}
            <Box
              sx={{
                position: "absolute",
                zIndex: 1,
                backgroundImage: `url(${backgroundPng})`,
                backgroundRepeat: "no-repeat",
                backgroundSize: "contain",
                backgroundPosition: "center",
                width: "70%",
                height: "100%",
                top: 0,
                right: 0,
              }}
            />
          </Box>
        </motion.div>
      </AnimatePresence>
    </Box>
  );
};

CurrentAthleteWidget.propTypes = {
  currentAthlete: PropTypes.any.isRequired,
  currentLift: PropTypes.any.isRequired,
  currentMouv: PropTypes.any.isRequired,
  attemptNumber: PropTypes.number.isRequired,
  records: PropTypes.array,
  showPR: PropTypes.bool.isRequired,
};

export default CurrentAthleteWidget;
